<template>
  <div v-if="visible" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-4xl flex flex-col" style="max-height: 92vh;">

      <!-- Cabecera modal -->
      <div class="flex items-center justify-between px-6 py-4 border-b shrink-0">
        <h2 class="text-lg font-bold text-gray-900">Hoja de Producción</h2>
        <div class="flex gap-2 items-center">
          <button @click="imprimir" class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition flex items-center gap-1.5">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
            </svg>
            Imprimir
          </button>
          <button @click="$emit('cerrar')" class="ml-1 text-gray-400 hover:text-gray-600 p-1">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Área imprimible -->
      <div ref="printArea" class="flex-1 overflow-y-auto p-6 bg-white">

        <h2 class="text-xl font-bold text-gray-900 mb-4">Hoja de Producción</h2>

        <!-- Datos Generales -->
        <section class="border border-gray-300 rounded mb-5">
          <div class="px-4 py-2 border-b border-gray-300 bg-gray-50">
            <span class="text-sm font-semibold text-gray-700">Datos Generales</span>
          </div>
          <div class="p-4">
            <!-- Fila 1: Lote / Vendedor / Cliente -->
            <div class="grid grid-cols-3 gap-4 mb-3 text-sm">
              <div>
                <span class="text-gray-500">Lote</span>
                <span class="ml-2 font-bold text-gray-900">{{ orden.id }}</span>
              </div>
              <div>
                <span class="text-gray-500">Vendedor</span>
                <span class="ml-2 font-bold text-gray-900">{{ header.vendedor ?? '—' }}</span>
              </div>
              <div>
                <span class="text-gray-500">Cliente</span>
                <span class="ml-2 font-bold text-gray-900">{{ header.cliente ?? '—' }}</span>
              </div>
            </div>

            <!-- Fila 2: Código de barras / Pedido / Fecha Entrega Pedido -->
            <div class="grid grid-cols-3 gap-4 mb-3 items-center text-sm">
              <div>
                <span class="block text-gray-500 mb-1">Codigo</span>
                <!-- Código de barras SVG generado en cliente usando el id -->
                <svg ref="barcodeEl" class="h-10"></svg>
              </div>
              <div>
                <span class="text-gray-500">Pedido</span>
                <span class="ml-2 font-bold text-gray-900">{{ header.folio ?? '—' }}</span>
              </div>
              <div class="space-y-1">
                <div>
                  <span class="text-gray-500">Fecha Entrega Pedido</span>
                  <span class="ml-2 font-bold text-gray-900">{{ formatFecha(header.fecha_entrega) }}</span>
                </div>
                <div>
                  <span class="text-gray-500">Fecha Creación Orden</span>
                  <span class="ml-2 font-bold text-gray-900">{{ formatFecha(orden.created_at) }}</span>
                </div>
              </div>
            </div>

            <!-- Fila 3: Diseñador / Tipo Entrega -->
            <div class="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span class="text-gray-500">Diseñador</span>
                <span class="ml-2 font-bold text-gray-900">{{ header.disenador ?? '—' }}</span>
              </div>
              <div></div>
              <div>
                <span class="text-gray-500">Tipo Entrega</span>
                <span class="ml-2 font-bold text-gray-900">{{ header.tipo_entrega === 'recepcion' ? 'Recepción' : 'Domicilio' }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- Detalle de Orden -->
        <section class="border border-gray-300 rounded">
          <div class="px-4 py-2 border-b border-gray-300 bg-gray-50">
            <span class="text-sm font-semibold text-gray-700">Detalle de Orden</span>
          </div>
          <table class="w-full text-xs text-left">
            <thead>
              <tr class="border-b border-gray-300 bg-gray-50">
                <th class="px-3 py-2 font-semibold text-gray-700">Componente</th>
                <th class="px-3 py-2 font-semibold text-gray-700 text-center">Cantidad</th>
                <th class="px-3 py-2 font-semibold text-gray-700 text-center">Ancho</th>
                <th class="px-3 py-2 font-semibold text-gray-700 text-center">Alto</th>
                <th class="px-3 py-2 font-semibold text-gray-700">Archivo</th>
                <th class="px-3 py-2 font-semibold text-gray-700">Acabados</th>
                <th class="px-3 py-2 font-semibold text-gray-700">Frente/Vuelta</th>
                <th class="px-3 py-2 font-semibold text-gray-700">Observaciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(det, i) in detalles" :key="i" class="border-t border-gray-200">
                <td class="px-3 py-2 font-medium text-gray-800">{{ det.producto_nombre_orig }}</td>
                <td class="px-3 py-2 text-center">{{ det.cantidad_piezas }}</td>
                <td class="px-3 py-2 text-center">{{ det.ancho_metros ?? '—' }}</td>
                <td class="px-3 py-2 text-center">{{ det.alto_metros ?? '—' }}</td>
                <td class="px-3 py-2">{{ det.nombre_archivo || '—' }}</td>
                <td class="px-3 py-2">{{ det.acabados || '—' }}</td>
                <td class="px-3 py-2">{{ det.frente_vuelta ?? '-' }}</td>
                <td class="px-3 py-2 text-gray-600">{{ det.comentarios || '—' }}</td>
              </tr>
              <tr v-if="detalles.length === 0">
                <td colspan="8" class="px-4 py-6 text-center text-gray-400">Sin detalles</td>
              </tr>
            </tbody>
          </table>
        </section>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'

const props = defineProps({
  visible: Boolean,
  orden:   { type: Object, default: () => ({}) },
})

defineEmits(['cerrar'])

const printArea  = ref(null)
const barcodeEl  = ref(null)

const header   = computed(() => props.orden?.snapshot_header   ?? {})
const detalles = computed(() => props.orden?.snapshot_detalles ?? [])

const formatFecha = (val) => {
  if (!val) return '—'
  return new Date(val).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })
}

// ── Código de barras Code 128 (generado sin librerías externas) ──────────────
// Encoda el id de la orden como número en Code 128C (pares de dígitos)
const renderBarcode = () => {
  if (!barcodeEl.value || !props.orden?.id) return

  const svg   = barcodeEl.value
  const value = String(props.orden.id).padStart(6, '0')

  // Tabla Code 128 (subconjunto B — ASCII imprimible)
  const CODE128B = {
    start: { bars: '11010010000', w: 11 },
    stop:  { bars: '1100011101011', w: 13 },
    chars: buildCode128BTable(),
  }

  const encoded  = encodeCode128B(value, CODE128B)
  const barWidth = 2
  const barHeight = 40
  const totalWidth = encoded.length * barWidth + 20

  svg.setAttribute('viewBox', `0 0 ${totalWidth} ${barHeight + 10}`)
  svg.setAttribute('width', totalWidth)
  svg.setAttribute('height', barHeight + 10)

  let x = 10
  let rects = ''
  for (let i = 0; i < encoded.length; i++) {
    if (encoded[i] === '1') {
      rects += `<rect x="${x}" y="0" width="${barWidth}" height="${barHeight}" fill="black"/>`
    }
    x += barWidth
  }
  svg.innerHTML = rects + `<text x="${totalWidth / 2}" y="${barHeight + 9}" text-anchor="middle" font-size="7" font-family="monospace" fill="#333">${value}</text>`
}

watch(() => [props.visible, props.orden?.id], async ([vis]) => {
  if (vis) {
    await nextTick()
    renderBarcode()
  }
}, { immediate: true })

// ── Lógica Code 128B simplificada ──────────────────────────────────────────
function buildCode128BTable() {
  const table = {}
  const patterns = [
    '11011001100','11001101100','11001100110','10010011000','10010001100',
    '10001001100','10011001000','10011000100','10001100100','11001001000',
    '11001000100','11000100100','10110011100','10011011100','10011001110',
    '10111001100','10011101100','10011100110','11001110010','11001011100',
    '11001001110','11011100100','11001110100','11101101110','11101001100',
    '11100101100','11100100110','11101100100','11100110100','11100110010',
    '11011011000','11011000110','11000110110','10100011000','10001011000',
    '10001000110','10110001000','10001101000','10001100010','11010001000',
    '11000101000','11000100010','10110111000','10110001110','10001101110',
    '10111011000','10111000110','10001110110','11101110110','11010001110',
    '11000101110','11011101000','11011100010','11011101110','11101011000',
    '11101000110','11100010110','11101101000','11101100010','11100011010',
    '11101111010','11001000010','11110001010','10100110000','10100001100',
    '10010110000','10010000110','10000101100','10000100110','10110010000',
    '10110000100','10011010000','10011000010','10000110100','10000110010',
    '11000010010','11001010000','11110111010','11000010100','10001111010',
    '10100111100','10010111100','10010011110','10111100100','10011110100',
    '10011110010','11110100100','11110010100','11110010010','11011011110',
    '11011110110','11110110110','10101111000','10100011110','10001011110',
    '10111101000','10111100010','11110101000','11110100010','10111011110',
    '10111101110','11101011110','11110101110','11010000100','11010010000',
    '11010011100','1100011101011',
  ]
  for (let i = 0; i <= 95; i++) {
    table[String.fromCharCode(i + 32)] = { pattern: patterns[i], value: i }
  }
  return table
}

function encodeCode128B(text, CODE128B) {
  const startPattern = '11010010000'
  const stopPattern  = '1100011101011'

  let checksum = 104 // Start B value
  let bars     = startPattern

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    const entry = CODE128B.chars[ch]
    if (entry) {
      bars     += entry.pattern
      checksum += entry.value * (i + 1)
    }
  }

  // Checksum symbol
  const checksumIdx = checksum % 103
  const allPatterns = Object.values(CODE128B.chars).map(e => e.pattern)
  bars += allPatterns[checksumIdx] ?? ''
  bars += stopPattern

  return bars
}

// ── Imprimir ────────────────────────────────────────────────────────────────
const imprimir = () => {
  const inner = printArea.value?.innerHTML ?? ''
  const win   = window.open('', '_blank', 'width=960,height=720')
  win.document.write(`<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>Hoja de Producción — ${props.orden?.numero_orden ?? ''}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:20px}
    h2{font-size:18px;font-weight:700;margin-bottom:14px}
    section{border:1px solid #d1d5db;border-radius:6px;margin-bottom:16px}
    .sec-hdr{background:#f9fafb;padding:7px 14px;border-bottom:1px solid #d1d5db;font-size:11px;font-weight:700}
    .grid3{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;padding:14px}
    .grid3 .label{color:#6b7280;font-size:11px}
    .grid3 .val{font-weight:700;font-size:13px}
    .divider{grid-column:1/-1;border-top:1px solid #e5e7eb;margin:4px 0}
    table{width:100%;border-collapse:collapse}
    th{background:#f3f4f6;padding:5px 10px;text-align:left;font-size:10px;font-weight:700;border-bottom:1px solid #d1d5db}
    td{padding:5px 10px;border-top:1px solid #f3f4f6;font-size:11px;vertical-align:top}
    svg{display:block}
    @media print{body{padding:8px}button{display:none}}
  </style>
</head>
<body>${inner}</body>
</html>`)
  win.document.close()
  win.focus()
  setTimeout(() => win.print(), 500)
}
</script>
